import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'reactstrap';
import { useBoolean } from 'usehooks-ts';

import Button from 'components/App/Button';
import AppBadge from 'components/App/AppBadge';
import AppAvatar from 'components/App/Avatar';
import Loading from 'components/App/Loading';
import ModalRequestAction from 'components/Modals/ModalRequestAction';

import { convertIsoDateToString } from 'utils';
import { DEPARTMENT_CEO } from 'constants/department';

import images from 'assets/images';

import styles from './DataTableInternalAccount.module.scss';

const TABLE_COLUMNS = [
  { key: 'user', head: 'Người dùng', minWidth: 360 },
  { key: 'department', head: 'Bộ phận', minWidth: 220 },
  { key: 'market', head: 'Thị trường', minWidth: 220 },
  { key: 'lastLogin', head: 'Lần đăng nhập cuối', minWidth: 180 },
  { key: 'actions', head: 'Thao tác', minWidth: 140 },
];

function DataTableInternalAccount(props) {
  const {
    isLoading,
    data,
    onEdit,
    actionDeleteInternalAccount,
    isLoadingDeleteInternalAccount,
    onDeleteSuccess,
  } = props;

  const {
    value: isOpenModalDelete,
    setFalse: onCloseModalDelete,
    setTrue: onOpenModalDelete,
  } = useBoolean(false);

  const [deleteId, setDeleteId] = React.useState(null);

  const onClickOpenModalDelete = useCallback((id) => {
    setDeleteId(id);
    onOpenModalDelete();
  }, [onOpenModalDelete]);

  const onClickConfirmDelete = useCallback(() => {
    if (deleteId) {
      actionDeleteInternalAccount({
        id: deleteId,
        callback: () => {
          onCloseModalDelete();
          setDeleteId(null);
          onDeleteSuccess();
        },
      });
    }
  }, [deleteId, actionDeleteInternalAccount, onCloseModalDelete, onDeleteSuccess]);

  const onEditAccount = useCallback(
    (id) => {
      onEdit(id);
    },
    [onEdit],
  );

  return (
    <div className={styles.Component}>
      <Loading loading={isLoading} loaderOnPage />

      {data.length === 0 && !isLoading ? (
        <div className="NoData">Không có dữ liệu</div>
      ) : (
        <Table responsive className="Table">
          <thead className="Table__Header">
            <tr className="Table__Header__Row">
              {TABLE_COLUMNS.map((column) => (
                <th
                  key={column.key}
                  className="Table__Header__Row__Content"
                  style={{
                    minWidth: `${column.minWidth}px`,
                  }}
                >
                  {column.head}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="Table__Body">
            {data.map((item) => (
              <tr className="Table__Body__Row" key={item._id}>
                <td className="Table__Body__Row__Content">
                  <div className="Table__Body__Row__Content__Info">
                    <div className="Table__Body__Row__Content__Image">
                      <AppAvatar
                        className="avatar-select"
                        name={item.fullName}
                      />
                    </div>

                    <div>
                      <div className="Table__Body__Row__Content__NameRow">
                        <span className="Table__Body__Row__Content__Name">
                          {item.fullName}
                        </span>

                        {item.department === DEPARTMENT_CEO && (
                          <AppBadge
                            color="info"
                            className="Table__Body__Row__Content__RoleBadge"
                          >
                            CEO
                          </AppBadge>
                        )}
                      </div>

                      <div className="Table__Body__Row__Content__Contact">
                        <span className="Table__Body__Row__Content__Label">
                          {item.email}
                        </span>

                        <span className="Table__Body__Row__Content__Label">
                          {item.phoneNumber}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>

                <td className="Table__Body__Row__Content">{item.department}</td>

                <td className="Table__Body__Row__Content">
                  {item.markets && item.markets.length > 0
                    ? item.markets
                      .map((market) => market.name?.trim())
                      .filter(Boolean)
                      .join(', ')
                    : 'N/A'}
                </td>

                <td className="Table__Body__Row__Content">
                  <span className="Table__Body__Row__Content__Date">
                    {item.lastLogin
                      ? convertIsoDateToString(item.lastLogin, 'DD/MM/YYYY HH:mm:ss')
                      : '-'}
                  </span>
                </td>

                <td className="Table__Body__Row__Content">
                  {item.department !== DEPARTMENT_CEO && (
                    <div className="Action">
                      <Button
                        classes="Button Button__Edit"
                        value={item._id}
                        onClick={onEditAccount}
                      >
                        Chỉnh sửa
                      </Button>

                      <Button
                        classes="Button Button__Delete"
                        value={item._id}
                        onClick={onClickOpenModalDelete}
                      >
                        Xóa
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <ModalRequestAction
        isOpen={isOpenModalDelete}
        icon={images.modalGarbageCan.default}
        textRequest={'Bạn có chắc chắn muốn xoá \n người dùng này không?'}
        onCloseModal={onCloseModalDelete}
        onConfirm={onClickConfirmDelete}
        isLoadingConfirm={isLoadingDeleteInternalAccount}
      />
    </div>
  );
}

DataTableInternalAccount.propTypes = {
  isLoading: PropTypes.bool,
  data: PropTypes.instanceOf(Array),
  onEdit: PropTypes.func,
  actionDeleteInternalAccount: PropTypes.func,
  isLoadingDeleteInternalAccount: PropTypes.bool,
  onDeleteSuccess: PropTypes.func,
};

DataTableInternalAccount.defaultProps = {
  isLoading: false,
  data: [],
  onEdit: () => {},
  actionDeleteInternalAccount: () => {},
  isLoadingDeleteInternalAccount: false,
  onDeleteSuccess: () => {},
};

export default memo(DataTableInternalAccount);
